import React from 'react';
import Select from 'react-select';
import styled from 'styled-components';
import { useIntl, defineMessages } from 'react-intl';
import { Text } from '@companycam/slab-web';

import { AssignedUserObject } from 'data/v3/data-contracts';
import Common from 'helpers/intl-common';
import { RESTRICTED } from 'helpers/roles';
import { REACT_SELECT_STYLES, USER_SELECT_STYLES } from 'helpers/styles';
import ImageRestricted from 'images/user_role_restricted.png';
import { useIgnorableConfirmationModal } from 'javascripts/hooks';
import SelectOption from 'components/shared/AssignUsers/SelectOption';
import NoOptionsMessage from 'components/shared/AssignUsers/NoOptionsMessage';

const messages = defineMessages({
  searchInputPlaceholder: {
    id: 'AssignUsers.searchInputPlaceholder',
    defaultMessage: 'Search for Users',
    description: 'placeholder text for assign users search input',
  },
  searchInProgressMessage: {
    id: 'AssignUsers.searchInProgressMessage',
    defaultMessage: 'Finding your people',
    description: 'message for when search is in progress',
  },
  title: {
    id: 'AssignUsers.restrictedUserConfirmationModal.title',
    defaultMessage: 'Assign Restricted User?',
    description: 'modal title',
  },
  confirmButton: {
    id: 'AssignUsers.restrictedUserConfirmationModal.confirmButton',
    defaultMessage: 'Yes, Assign',
    description: 'confirm button label',
  },
  restrictedUserModalMessage: {
    id: 'AssignUsers.restrictedUserConfirmationModal.message',
    defaultMessage:
      'Assigning this user gives them access to the project. They will be able to view Photos, add Photos, join Conversations, and access Reports and Checklists.',
    description: 'modal message',
  },
  restrictedUserModalSubtitle: {
    id: 'AssignUsers.restrictedUserConfirmationModal.subtitle',
    defaultMessage: '{name} is a Restricted user',
    description: 'modal subtitle',
  },
});

// #region ReactSelect
const ReactSelectStyles = {
  ...REACT_SELECT_STYLES,
  ...USER_SELECT_STYLES,
};

const updateInputValue = (newValue: string) => newValue;
const getOptionValue = (user: AssignedUserObject) => user?.id.toString();
const getOptionLabel = (user: AssignedUserObject) =>
  user?.fullName || user?.email;
// #endregion

// #region styled-components
const SearchIcon = styled.i`
  color: var(--cc_color_text_nonessential);
  font-size: 22px;
  margin: 0 12px 0 var(--cc_size_spacing_s);
`;
const SelectMessage = styled.div`
  padding: var(--cc_size_spacing_m);
  display: grid;
  gap: var(--cc_size_spacing_s);
  place-items: center;
  text-align: center;
  line-height: 1.25;
`;
const SearchResultsIcon = styled.i`
  font-size: 28px;
  color: var(--cc_color_text_nonessential);
`;
// #endregion

type UserSelectProps = {
  assignedUserIds: Set<AssignedUserObject['id']>;
  assignableUsers: AssignedUserObject[];
  onAssignUser: (user: AssignedUserObject) => void;
  hasGetAssignableUsersError: boolean;
};

type OptionProps = {
  data: AssignedUserObject;
  innerRef: React.RefCallback<HTMLDivElement>;
  innerProps: React.HTMLAttributes<HTMLDivElement>;
  isFocused: boolean;
  isSelected: boolean;
};

const UserSelect = ({
  assignedUserIds,
  assignableUsers,
  onAssignUser,
  hasGetAssignableUsersError,
}: UserSelectProps) => {
  const intl = useIntl();

  const { doActionOrShowConfirmationModal } = useIgnorableConfirmationModal({
    confirmationKey: 'assignRestrictedUserToChecklist',
    confirmationAction: onAssignUser,
    modalProps: {
      title: intl.formatMessage(messages.title),
      image: ImageRestricted,
      message: intl.formatMessage(messages.restrictedUserModalMessage),
      confirmLabel: intl.formatMessage(messages.confirmButton),
      cancelLabel: intl.formatMessage(Common.cancel),
      destructive: false,
    },
  });

  const handleAssignUser = (user: AssignedUserObject) => {
    if (user.role === RESTRICTED) {
      doActionOrShowConfirmationModal(user, {
        subtitle: intl.formatMessage(messages.restrictedUserModalSubtitle, {
          name: user.fullName,
        }),
      });
    } else {
      onAssignUser(user);
    }
  };

  const getIsOptionSelected = (user: AssignedUserObject) =>
    assignedUserIds.has(user.id);

  return (
    <div onClick={(e: React.SyntheticEvent) => e.stopPropagation()}>
      <Select
        id="users-assignable"
        name="search-users"
        options={assignableUsers}
        // @ts-expect-error
        cacheOptions
        value={null}
        getOptionLabel={getOptionLabel}
        getOptionValue={getOptionValue}
        onInputChange={updateInputValue}
        onChange={handleAssignUser}
        isOptionSelected={getIsOptionSelected}
        hideSelectedOptions
        noOptionsMessage={() => (
          <NoOptionsMessage
            hasGetAssignableUsersError={hasGetAssignableUsersError}
          />
        )}
        placeholder={intl.formatMessage(messages.searchInputPlaceholder)}
        menuPortalTarget={document.body}
        styles={ReactSelectStyles}
        components={{
          DropdownIndicator: () => (
            <SearchIcon className="mdi mdi-magnify" aria-hidden="true" />
          ),
          LoadingMessage: () => (
            <div style={{ padding: 8 }}>
              <SelectMessage>
                <SearchResultsIcon className="mdi mdi-face-recognition" />
                <Text as="p">
                  {intl.formatMessage(messages.searchInProgressMessage)}&hellip;
                </Text>
              </SelectMessage>
            </div>
          ),
          IndicatorSeparator: () => null,
          Option: ({
            data: { fullName, email, role },
            innerRef,
            innerProps,
            isFocused,
            isSelected,
          }: OptionProps) => (
            <SelectOption
              innerRef={innerRef}
              innerProps={innerProps}
              primaryDescription={fullName || email}
              secondaryDescription={role === RESTRICTED ? role : ''}
              isFocused={isFocused}
              isSelected={isSelected}
            />
          ),
        }}
      />
    </div>
  );
};
export default UserSelect;
